/**
 * The cat's dynamic impact body.
 *
 * Normal movement stays on the precise kinematic controller. These articulated
 * bodies always exist but are disabled until an impact, which avoids allocating
 * physics objects during a busy chase and guarantees that only one cat collider
 * participates at a time.
 */

import type RAPIER from '@dimforge/rapier3d-compat';
import type { Vec3 } from '@/core/math';
import { clone, vec3 } from '@/core/math';
import { tuning, type CatRagdollTuning } from '@/core/tuning';
import { Groups, RAPIER as Rapier, type PhysicsWorld } from '@/engine/physicsWorld';
import {
  createSkeletonDescription,
  LEG_ORDER,
  type LegName,
  type SkeletonDescription,
} from '@/cat/quadrupedLocomotion';

export type RagdollReason = 'spray' | 'fall' | 'catch' | 'vacuum' | 'debug';

export interface Quaternion {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface RagdollLegPose {
  root: Vec3;
  knee: Vec3;
  foot: Vec3;
}

export interface RagdollVisualPose {
  objectPosition: Vec3;
  objectRotation: Quaternion;
  head: { position: Vec3; rotation: Quaternion };
  legs: Record<LegName, RagdollLegPose>;
}

export interface RagdollRecoveryState {
  elapsed: number;
  speed: number;
  allSleeping: boolean;
}

interface LegBodies {
  upper: RAPIER.RigidBody;
  lower: RAPIER.RigidBody;
}

/** Pure recovery policy, separated so timing edge cases do not need Rapier. */
export function shouldRecoverRagdoll(
  state: RagdollRecoveryState,
  config: CatRagdollTuning = tuning.cat.ragdoll,
): boolean {
  if (state.elapsed >= config.maxDuration) return true;
  if (state.elapsed < config.minDuration) return false;
  return state.allSleeping || state.speed <= config.settleSpeed;
}

export class CatRagdoll {
  private readonly torso: RAPIER.RigidBody;
  private readonly head: RAPIER.RigidBody;
  private readonly legs: Record<LegName, LegBodies>;
  private readonly bodies: RAPIER.RigidBody[];
  private readonly joints: RAPIER.ImpulseJoint[] = [];
  private elapsed = 0;
  private enabled = false;

  constructor(
    private readonly physics: PhysicsWorld,
    private readonly skeleton: SkeletonDescription = createSkeletonDescription(),
    private readonly config: CatRagdollTuning = tuning.cat.ragdoll,
  ) {
    this.torso = this.createBody(
      Rapier.ColliderDesc.cuboid(
        config.torsoSize[0] / 2,
        config.torsoSize[1] / 2,
        config.torsoSize[2] / 2,
      ),
      config.torsoMass,
    );
    this.head = this.createBody(Rapier.ColliderDesc.ball(config.headRadius), config.headMass);

    const segmentLength = skeleton.legLength / 2;
    const halfHeight = Math.max(0, segmentLength / 2 - config.legRadius);
    this.legs = {
      frontLeft: this.createLeg(halfHeight),
      frontRight: this.createLeg(halfHeight),
      hindLeft: this.createLeg(halfHeight),
      hindRight: this.createLeg(halfHeight),
    };
    this.bodies = [
      this.torso,
      this.head,
      ...LEG_ORDER.flatMap((name) => [this.legs[name].upper, this.legs[name].lower]),
    ];

    const spinePivot = tupleToVec3(tuning.cat.locomotion.skeleton.spinePivot);
    const headOffset = tupleToVec3(tuning.cat.locomotion.skeleton.headOffset);
    this.addJoint(this.torso, this.head, subtract(headOffset, spinePivot), vec3());

    for (const name of LEG_ORDER) {
      const root = skeleton.legRootOffsets[name];
      const leg = this.legs[name];
      this.addJoint(
        this.torso,
        leg.upper,
        subtract(root, spinePivot),
        vec3(0, segmentLength / 2, 0),
      );
      this.addJoint(
        leg.upper,
        leg.lower,
        vec3(0, -segmentLength / 2, 0),
        vec3(0, segmentLength / 2, 0),
      );
    }
  }

  get active(): boolean {
    return this.enabled;
  }

  get bodyCount(): number {
    return this.bodies.length;
  }

  get jointCount(): number {
    return this.joints.length;
  }

  get linearVelocity(): Vec3 {
    return clone(this.torso.linvel());
  }

  get headPosition(): Vec3 {
    return clone(this.head.translation());
  }

  get torsoPosition(): Vec3 {
    return clone(this.torso.translation());
  }

  /** Enables and poses every bone before applying the impact to the torso. */
  activate(base: Vec3, yaw: number, inheritedVelocity: Vec3, impulse: Vec3): void {
    if (this.enabled) {
      this.addImpact(impulse);
      return;
    }

    this.enabled = true;
    this.elapsed = 0;
    const rotation = quaternionFromYaw(yaw);
    const spinePivot = tupleToVec3(tuning.cat.locomotion.skeleton.spinePivot);
    const headOffset = tupleToVec3(tuning.cat.locomotion.skeleton.headOffset);
    const segmentLength = this.skeleton.legLength / 2;

    this.setBodyTransform(
      this.torso,
      localToWorld(spinePivot, base, rotation),
      rotation,
      inheritedVelocity,
    );
    this.setBodyTransform(
      this.head,
      localToWorld(headOffset, base, rotation),
      rotation,
      inheritedVelocity,
    );

    for (const name of LEG_ORDER) {
      const root = this.skeleton.legRootOffsets[name];
      const knee = vec3(root.x, root.y - segmentLength, root.z);
      const foot = vec3(root.x, root.y - this.skeleton.legLength, root.z);
      const leg = this.legs[name];
      this.setBodyTransform(
        leg.upper,
        localToWorld(midpoint(root, knee), base, rotation),
        rotation,
        inheritedVelocity,
      );
      this.setBodyTransform(
        leg.lower,
        localToWorld(midpoint(knee, foot), base, rotation),
        rotation,
        inheritedVelocity,
      );
    }

    this.torso.applyImpulse(impulse, true);
  }

  /** Adds a second knock without rebuilding or reposing the active ragdoll. */
  addImpact(impulse: Vec3): void {
    if (!this.enabled) return;
    this.elapsed = 0;
    this.torso.applyImpulse(impulse, true);
    for (const body of this.bodies) body.wakeUp();
  }

  advance(dt: number): void {
    if (this.enabled) this.elapsed += dt;
  }

  shouldRecover(): boolean {
    return shouldRecoverRagdoll(
      {
        elapsed: this.elapsed,
        speed: this.maxBodySpeed(),
        allSleeping: this.bodies.every((body) => body.isSleeping()),
      },
      this.config,
    );
  }

  /** Floor-aligned controller base chosen from the current physical torso. */
  recoveryPosition(fallbackY: number): Vec3 {
    const visual = this.visualPose();
    const ground = this.physics.groundHeightAt(
      visual.objectPosition.x,
      visual.objectPosition.z,
      this.torso.translation().y + this.config.recoveryGroundProbeHeight,
    );
    return vec3(visual.objectPosition.x, ground ?? fallbackY, visual.objectPosition.z);
  }

  /** Disables all physical bones; the world retains them for the next impact. */
  deactivate(): void {
    this.enabled = false;
    this.elapsed = 0;
    for (const body of this.bodies) {
      body.setLinvel(vec3(), false);
      body.setAngvel(vec3(), false);
      body.setEnabled(false);
    }
  }

  /** Converts physical bone transforms to the renderer-free primitive-rig pose. */
  visualPose(): RagdollVisualPose {
    const rootRotation = cloneQuaternion(this.torso.rotation());
    const spinePivot = tupleToVec3(tuning.cat.locomotion.skeleton.spinePivot);
    const objectPosition = subtract(
      this.torso.translation(),
      rotateVector(rootRotation, spinePivot),
    );
    const rootInverse = conjugate(rootRotation);
    const segmentLength = this.skeleton.legLength / 2;

    return {
      objectPosition,
      objectRotation: rootRotation,
      head: {
        position: worldToLocal(this.head.translation(), objectPosition, rootInverse),
        rotation: multiplyQuaternions(rootInverse, this.head.rotation()),
      },
      legs: {
        frontLeft: this.legVisualPose(
          this.legs.frontLeft,
          segmentLength,
          objectPosition,
          rootInverse,
        ),
        frontRight: this.legVisualPose(
          this.legs.frontRight,
          segmentLength,
          objectPosition,
          rootInverse,
        ),
        hindLeft: this.legVisualPose(
          this.legs.hindLeft,
          segmentLength,
          objectPosition,
          rootInverse,
        ),
        hindRight: this.legVisualPose(
          this.legs.hindRight,
          segmentLength,
          objectPosition,
          rootInverse,
        ),
      },
    };
  }

  private createBody(colliderDesc: RAPIER.ColliderDesc, mass: number): RAPIER.RigidBody {
    const body = this.physics.world.createRigidBody(
      Rapier.RigidBodyDesc.dynamic()
        .setEnabled(false)
        .setLinearDamping(this.config.linearDamping)
        .setAngularDamping(this.config.angularDamping)
        .setAdditionalSolverIterations(this.config.solverIterations),
    );
    colliderDesc
      .setCollisionGroups(Groups.ragdoll)
      .setMass(mass)
      .setFriction(this.config.friction)
      .setRestitution(this.config.restitution);
    this.physics.world.createCollider(colliderDesc, body);
    return body;
  }

  private createLeg(halfHeight: number): LegBodies {
    return {
      upper: this.createBody(
        Rapier.ColliderDesc.capsule(halfHeight, this.config.legRadius),
        this.config.legSegmentMass,
      ),
      lower: this.createBody(
        Rapier.ColliderDesc.capsule(halfHeight, this.config.legRadius),
        this.config.legSegmentMass,
      ),
    };
  }

  private addJoint(
    parent: RAPIER.RigidBody,
    child: RAPIER.RigidBody,
    parentAnchor: Vec3,
    childAnchor: Vec3,
  ): void {
    const joint = this.physics.world.createImpulseJoint(
      Rapier.JointData.spherical(parentAnchor, childAnchor),
      parent,
      child,
      false,
    );
    joint.setContactsEnabled(false);
    this.joints.push(joint);
  }

  private setBodyTransform(
    body: RAPIER.RigidBody,
    position: Vec3,
    rotation: Quaternion,
    velocity: Vec3,
  ): void {
    body.setEnabled(true);
    body.setTranslation(position, false);
    body.setRotation(rotation, false);
    body.setLinvel(velocity, false);
    body.setAngvel(vec3(), false);
    body.wakeUp();
  }

  private maxBodySpeed(): number {
    let speed = 0;
    for (const body of this.bodies) {
      const velocity = body.linvel();
      speed = Math.max(speed, Math.hypot(velocity.x, velocity.y, velocity.z));
    }
    return speed;
  }

  private legVisualPose(
    leg: LegBodies,
    segmentLength: number,
    objectPosition: Vec3,
    rootInverse: Quaternion,
  ): RagdollLegPose {
    const upperCenter = leg.upper.translation();
    const upperDirection = rotateVector(leg.upper.rotation(), vec3(0, segmentLength / 2, 0));
    const lowerCenter = leg.lower.translation();
    const lowerDirection = rotateVector(leg.lower.rotation(), vec3(0, segmentLength / 2, 0));
    const upperBottom = subtract(upperCenter, upperDirection);
    const lowerTop = add(lowerCenter, lowerDirection);

    return {
      root: worldToLocal(add(upperCenter, upperDirection), objectPosition, rootInverse),
      knee: worldToLocal(midpoint(upperBottom, lowerTop), objectPosition, rootInverse),
      foot: worldToLocal(subtract(lowerCenter, lowerDirection), objectPosition, rootInverse),
    };
  }
}

function quaternionFromYaw(yaw: number): Quaternion {
  return { x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) };
}

function cloneQuaternion(value: RAPIER.Rotation): Quaternion {
  return { x: value.x, y: value.y, z: value.z, w: value.w };
}

function conjugate(value: Quaternion): Quaternion {
  return { x: -value.x, y: -value.y, z: -value.z, w: value.w };
}

function multiplyQuaternions(a: Quaternion, b: RAPIER.Rotation): Quaternion {
  return {
    x: a.w * b.x + a.x * b.w + a.y * b.z - a.z * b.y,
    y: a.w * b.y - a.x * b.z + a.y * b.w + a.z * b.x,
    z: a.w * b.z + a.x * b.y - a.y * b.x + a.z * b.w,
    w: a.w * b.w - a.x * b.x - a.y * b.y - a.z * b.z,
  };
}

function rotateVector(rotation: RAPIER.Rotation, value: Vec3): Vec3 {
  const doubledCross = vec3(
    (rotation.y * value.z - rotation.z * value.y) * 2,
    (rotation.z * value.x - rotation.x * value.z) * 2,
    (rotation.x * value.y - rotation.y * value.x) * 2,
  );
  return vec3(
    value.x +
      rotation.w * doubledCross.x +
      rotation.y * doubledCross.z -
      rotation.z * doubledCross.y,
    value.y +
      rotation.w * doubledCross.y +
      rotation.z * doubledCross.x -
      rotation.x * doubledCross.z,
    value.z +
      rotation.w * doubledCross.z +
      rotation.x * doubledCross.y -
      rotation.y * doubledCross.x,
  );
}

function localToWorld(local: Vec3, origin: Vec3, rotation: Quaternion): Vec3 {
  return add(origin, rotateVector(rotation, local));
}

function worldToLocal(world: RAPIER.Vector, origin: Vec3, inverseRotation: Quaternion): Vec3 {
  return rotateVector(inverseRotation, subtract(world, origin));
}

function add(a: RAPIER.Vector, b: Vec3): Vec3 {
  return vec3(a.x + b.x, a.y + b.y, a.z + b.z);
}

function subtract(a: RAPIER.Vector, b: Vec3): Vec3 {
  return vec3(a.x - b.x, a.y - b.y, a.z - b.z);
}

function midpoint(a: Vec3, b: Vec3): Vec3 {
  return vec3((a.x + b.x) / 2, (a.y + b.y) / 2, (a.z + b.z) / 2);
}

function tupleToVec3(tuple: [number, number, number]): Vec3 {
  return vec3(tuple[0], tuple[1], tuple[2]);
}
